import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, Briefcase, Link2, Unlink, Loader2, ExternalLink, X } from "lucide-react";
import { API_BASE, formatApiError } from "./AuthContext";
import { useTeamMembers } from "./AssigneePicker";

function ClientSearch({ onPick }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setRows([]); return undefined; }
    const id = setTimeout(() => axios.get(`${API_BASE}/clients`, { params: { q: q.trim() } }).then((r) => setRows(r.data.slice(0, 8))).catch(() => setRows([])), 250);
    return () => clearTimeout(id);
  }, [q]);
  return (
    <div className="relative flex-1">
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients by name, email or phone…" data-testid="triage-client-search"
        className="w-full bg-white border border-[var(--border)] rounded px-2.5 py-1.5 text-[12px] focus:outline-none focus:border-[var(--ink)]" />
      {rows.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-[var(--border)] rounded shadow-md max-h-56 overflow-y-auto">
          {rows.map((c) => (
            <button key={c.client_id} onClick={() => { onPick(c); setQ(""); setRows([]); }} data-testid={`triage-client-option-${c.client_id}`}
              className="w-full text-left px-2.5 py-1.5 text-[12px] hover:bg-[var(--subtle)]">
              <b>{c.name}</b> <span className="text-[var(--ink-3)] font-mono">{c.primary_contact_email || ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function JobDialog({ email, client, onClose, onCreated }) {
  const members = useTeamMembers();
  const [title, setTitle] = useState((email.subject || "New job").slice(0, 240));
  const [assignee, setAssignee] = useState("");
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/email-triage/${email.email_id}/create-job`, {
        title, client_id: client?.client_id || null, assignee_id: assignee || null, due_at: due || null,
      });
      toast.success(`Job created: ${r.data.title}`); onCreated(r.data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const cls = "w-full bg-white border border-[var(--border)] rounded px-2.5 py-1.5 text-[12px] focus:outline-none focus:border-[var(--ink)]";
  return (
    <div className="card p-3 space-y-2 bg-[var(--subtle)]" data-testid="triage-job-dialog">
      <div className="flex items-center justify-between"><div className="overline text-[10px]">New job from this email{client ? ` · ${client.name}` : ""}</div>
        <button onClick={onClose} data-testid="triage-job-cancel"><X size={13} /></button></div>
      <input value={title} onChange={(e) => setTitle(e.target.value)} className={cls} data-testid="triage-job-title" />
      <div className="flex gap-2">
        <select value={assignee} onChange={(e) => setAssignee(e.target.value)} className={cls} data-testid="triage-job-assignee">
          <option value="">Assign to me</option>
          {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
        </select>
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className={cls} data-testid="triage-job-due" />
      </div>
      <button onClick={submit} disabled={busy || !title.trim()} className="btn-primary w-full justify-center text-[12px]" data-testid="triage-job-submit">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Briefcase size={12} />} Create job
      </button>
    </div>
  );
}

// v68.51 — explicit "Attach to client" + one-click blank job on every triage email.
export default function TriageClientJobBar({ email, onChanged }) {
  const [client, setClient] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [showJob, setShowJob] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setClient(null);
    if (email.client_id) axios.get(`${API_BASE}/clients/${email.client_id}`).then((r) => setClient(r.data)).catch(() => {});
    axios.get(`${API_BASE}/email-triage/${email.email_id}/jobs`).then((r) => setJobs(r.data)).catch(() => setJobs([]));
  }, [email.email_id, email.client_id]);

  const attach = async (c) => {
    setBusy(true);
    try { await axios.post(`${API_BASE}/email-triage/${email.email_id}/attach-client`, { client_id: c.client_id }); setClient(c); toast.success(`Filed under ${c.name} → Communications`); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const detach = async () => {
    setBusy(true);
    try { await axios.post(`${API_BASE}/email-triage/${email.email_id}/detach-client`); setClient(null); toast.success("Unlinked from client"); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-[var(--border)] p-3 space-y-2" data-testid="triage-client-job-bar">
      <div className="flex items-center gap-2 flex-wrap">
        <Building2 size={13} className="text-[var(--ink-3)]" />
        {client ? (
          <>
            <span className="text-[12px]" data-testid="triage-linked-client">Filed under <b>{client.name}</b></span>
            <a href={`/clients?client=${client.client_id}&tab=comms`} target="_blank" rel="noreferrer" data-testid="triage-open-client-comms"
              className="text-[11px] text-[var(--accent)] hover:underline inline-flex items-center gap-0.5">Communications <ExternalLink size={10} /></a>
            <button onClick={detach} disabled={busy} className="text-[11px] text-[var(--ink-3)] hover:text-[#B91C1C] inline-flex items-center gap-0.5" data-testid="triage-detach-client"><Unlink size={10} /> Unlink</button>
          </>
        ) : (
          <><span className="text-[12px] text-[var(--ink-2)] inline-flex items-center gap-1"><Link2 size={11} /> Attach to a client</span><ClientSearch onPick={attach} /></>
        )}
        <button onClick={() => setShowJob((v) => !v)} className="btn-secondary !py-1 !px-2.5 text-[12px] ml-auto" data-testid="triage-create-job-btn"><Briefcase size={12} /> Create job</button>
      </div>
      {showJob && <JobDialog email={email} client={client} onClose={() => setShowJob(false)} onCreated={(j) => { setShowJob(false); setJobs((x) => [j, ...x]); onChanged?.(); }} />}
      {jobs.length > 0 && (
        <div className="text-[11px] flex flex-wrap gap-2" data-testid="triage-email-jobs">
          <span className="text-[var(--ink-3)]">Jobs from this email:</span>
          {jobs.map((j) => <a key={j.job_id} href={`/jobs?job_id=${j.job_id}`} className="underline" data-testid={`triage-email-job-${j.job_id}`}>{j.title}</a>)}
        </div>
      )}
    </div>
  );
}
